import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { formatDate, createHash } from "utils/helperFunctions";

import CALL from "_DatabaseFunctions/cloudCalls";
import DB from "_DatabaseFunctions/directConnect";

import { Button } from "@mui/material";

import ModalComp from "app/Components/Modals/ModalTemplate";

interface GameInfo {
  id: string;
  gameName: string;
  sessionId: string;
  playerlist: {
    [key: string]: {
      manuallyAdded: boolean;
    };
  };
}

interface PlayerData {
  id: string;
  displayName: string;
}

interface ModalDets {
  open: boolean;
  status: string;
  playerData: PlayerData;
}

interface RootState {
  user: {
    user: {
      uid: string;
    };
  };
  requests: {
    receivedRequests: {
      [key: string]: any;
    };
  };
}

function WaitlistModal({
  dets,
  gameInfo,
  callback,
}: {
  dets: ModalDets;
  gameInfo: GameInfo;
  callback?: () => void;
}) {
  const { playerData } = dets;
  const user = useSelector((state: RootState) => state.user.user);
  const requests = useSelector(
    (state: RootState) => state.requests.receivedRequests,
  );
  const [open, setShow] = useState(false);
  const [modalData, setModalData] = useState<{
    status?: string;
    title?: string;
    msg?: string;
    type?: string;
    action?: string;
  }>({});

  useEffect(() => {
    if (dets.open === true) {
      setShow(true);
      if (dets.status && dets.status.trim() !== "") {
        renderModalInfo(dets.status);
      }
    }
  }, [dets]);

  const acceptPlayer = async () => {
    // check waitlist.playerlist first to see if player exists or is added by host
    const { manuallyAdded } = gameInfo.playerlist[playerData.id];
    console.log(`manuallyAdded`, manuallyAdded);
    if (!manuallyAdded) {
      const reqId = createHash(playerData.id + gameInfo.id);
      const reqData = requests[reqId];
      DB.sendConfirmationPendingNotification(reqData);
      const numMin = 15;
      // ?Move to server
      const reqExpTime = Date.now() + numMin * 60000;
      DB.pendingConfirmReq(reqId, reqExpTime);
      DB.addPlayerToGame(gameInfo.id, playerData, reqExpTime);

      CALL.sendConfirmationNeededEmail(
        playerData.id,
        gameInfo.gameName,
        formatDate(reqExpTime),
      );

      CALL.sendConfirmationNeededText(
        playerData.id,
        gameInfo.gameName,
        formatDate(reqExpTime),
      );
    } else {
      const startedAt = Date.now();
      DB.startPlayerSession({
        gameSessionId: gameInfo.sessionId,
        hostId: user.uid,
        gameId: gameInfo.id,
        gameName: gameInfo.gameName,
        playerId: playerData.id,
        playerName: playerData.displayName,
      });
      DB.addPlayerToGame(gameInfo.id, playerData, startedAt);
    }
    DB.removeFromWaitlist(gameInfo.id, playerData.id);
    
    // Send success notification
    DB.sendPlayerAddedSuccessNotification(
      user.uid,
      playerData.displayName,
      gameInfo.gameName
    );
  };

  const removePlayer = async (event: React.MouseEvent) => {
    const reqId = createHash(playerData.id + gameInfo.id);
    DB.removeFromWaitlist(gameInfo.id, playerData.id);
    DB.deleteRequest(reqId);
  };

  const renderModalInfo = (status: string) => {
    switch (status) {
      case "addPlayer":
        setModalData({
          status,
          title: "Add Player to Game",
          msg: `Add ${playerData.displayName} to Game?`,
          type: "info",
        });
        break;
      case "removePlayer":
        setModalData({
          status,
          title: "Remove Player",
          msg: `Remove ${playerData.displayName} from Waitlist?`,
          type: "info",
        });
        break;
      case "warning":
        setModalData({
          status,
          title: "Error Adding Player",
          msg: `Game Must be Running Before Players are Added to Table`,
          type: "error",
        });
        break;
      default:
        console.warn(`WaitlistModal: Unhandled status "${status}"`);
        setModalData({
          title: "Unknown Action",
          msg: "An unknown action was requested. Please try again.",
          type: "error",
        });
        break;
    }
  };

  const modalChildren = () => {
    if (
      modalData.status === "addPlayer" ||
      modalData.status === "removePlayer"
    ) {
      const ChildMap: Record<
        string,
        { callback: (...args: any[]) => void | Promise<void>; msg: string }
      > = {
        addPlayer: {
          callback: acceptPlayer,
          msg: `${playerData.displayName} Added Successfully!`,
        },
        removePlayer: {
          callback: removePlayer,
          msg: `${playerData.displayName} Removed Successfully!`,
        },
      };
      const { callback, msg } = ChildMap[modalData.status];
      return (
        <div>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setModalData({
                type: "success",
                title: "Success",
                msg,
              });
              callback();
            }}
          >
            Confirm
          </Button>
          <Button variant="outlined" color="inherit" onClick={() => setShow(false)}>
            Close
          </Button>
        </div>
      );
    }
    return (
      <Button variant="outlined" color="inherit" onClick={() => setShow(false)}>
        Close
      </Button>
    );
  };

  return (
    <ModalComp
      title={modalData.title}
      modalType={modalData.type}
      msg={modalData.msg}
      show={open}
      onClose={() => setShow(false)}
      action={modalData.action}
    >
      {modalChildren()}
    </ModalComp>
  );
}

export default WaitlistModal;
